import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { X, Plus, ImagePlus, Eye, Film } from "lucide-react";
import { toast } from "sonner";

export const StoriesBar = () => {
  const { api, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const STORY_DURATION = 6000; // 6 seconds per story

  const isArtist = user?.role === "artist";

  const fetchStories = useCallback(async () => {
    try {
      const res = await api.get("/stories");
      setGroups(res.data.story_groups || []);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const isVideo = file.type.startsWith("video/");
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview({ url: ev.target.result, type: isVideo ? "video" : "image" });
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (!newContent.trim() && !imageFile) return;
    setLoading(true);
    try {
      let media_url = null;
      let media_type = null;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const uploadRes = await api.post("/uploads", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        media_url = uploadRes.data.url;
        media_type = imageFile.type.startsWith("video/") ? "video" : "image";
      }
      await api.post("/stories", { content: newContent, media_url, media_type });
      setNewContent("");
      setImageFile(null);
      setImagePreview(null);
      setCreating(false);
      fetchStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setLoading(false);
  };

  const markViewed = useCallback(async (storyId) => {
    try {
      await api.post(`/stories/${storyId}/view`);
    } catch (err) { console.error(err); }
  }, [api]);

  const openStory = (groupIdx) => {
    setViewing({ groupIdx, storyIdx: 0 });
    setProgress(0);
  };

  // Auto-progress timer
  useEffect(() => {
    if (viewing === null) {
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
      return;
    }

    const group = groups[viewing.groupIdx];
    if (!group) return;
    const story = group.stories[viewing.storyIdx];
    if (!story) return;

    // Mark as viewed
    markViewed(story.id);

    const startTime = Date.now();
    setProgress(0);

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min((elapsed / STORY_DURATION) * 100, 100);
      setProgress(pct);

      if (pct >= 100) {
        // Auto-advance
        if (viewing.storyIdx < group.stories.length - 1) {
          setViewing({ ...viewing, storyIdx: viewing.storyIdx + 1 });
        } else if (viewing.groupIdx < groups.length - 1) {
          setViewing({ groupIdx: viewing.groupIdx + 1, storyIdx: 0 });
        } else {
          setViewing(null);
        }
        return;
      }
      progressRef.current = requestAnimationFrame(animate);
    };

    progressRef.current = requestAnimationFrame(animate);

    return () => {
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
    };
  }, [viewing, groups, markViewed]);

  const nextStory = () => {
    if (!viewing) return;
    const group = groups[viewing.groupIdx];
    if (viewing.storyIdx < group.stories.length - 1) {
      setViewing({ ...viewing, storyIdx: viewing.storyIdx + 1 });
      setProgress(0);
    } else if (viewing.groupIdx < groups.length - 1) {
      setViewing({ groupIdx: viewing.groupIdx + 1, storyIdx: 0 });
      setProgress(0);
    } else {
      setViewing(null);
    }
  };

  const prevStory = () => {
    if (!viewing) return;
    if (viewing.storyIdx > 0) {
      setViewing({ ...viewing, storyIdx: viewing.storyIdx - 1 });
      setProgress(0);
    } else if (viewing.groupIdx > 0) {
      const prevGroup = groups[viewing.groupIdx - 1];
      setViewing({ groupIdx: viewing.groupIdx - 1, storyIdx: prevGroup.stories.length - 1 });
      setProgress(0);
    }
  };

  if (groups.length === 0 && !isArtist) return null;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    return `${hours}h`;
  };

  return (
    <>
      {/* Stories bar */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="stories-bar">
        {isArtist && (
          <button
            onClick={() => setCreating(true)}
            className="flex flex-col items-center gap-1 shrink-0"
            data-testid="create-story-btn"
          >
            <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#FF6B35]/50 flex items-center justify-center hover:border-[#FF6B35] transition-colors">
              <Plus className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <span className="text-[10px] text-muted-foreground">Story</span>
          </button>
        )}

        {groups.map((group, idx) => (
          <button
            key={group.artist_id}
            onClick={() => openStory(idx)}
            className="flex flex-col items-center gap-1 shrink-0"
            data-testid={`story-bubble-${group.artist_id}`}
          >
            <div className={`w-16 h-16 rounded-full p-[2px] ${group.has_unviewed ? "bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3]" : "bg-[#6E6680]/40"}`}>
              <div className="w-full h-full rounded-full bg-background p-[2px]">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center overflow-hidden">
                  {group.artist_avatar ? (
                    <img src={group.artist_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-heading font-bold text-sm">
                      {(group.artist_name || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground max-w-16 truncate">
              {group.artist_name}
            </span>
          </button>
        ))}
      </div>

      {/* Create story modal */}
      {creating && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="create-story-modal">
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-foreground">Nouvelle Story</h3>
              <button onClick={() => { setCreating(false); setImageFile(null); setImagePreview(null); }} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {imagePreview && (
              <div className="relative">
                {imagePreview.type === "video" ? (
                  <video src={imagePreview.url} className="w-full h-48 object-cover rounded-xl" muted />
                ) : (
                  <img src={imagePreview.url} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                )}
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="absolute top-2 right-2 bg-black/60 rounded-full p-1"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            )}

            <textarea
              placeholder="Quoi de neuf ? (disparait dans 24h)"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              className="w-full bg-foreground/5 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-24"
              data-testid="story-content-input"
              maxLength={280}
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#FF6B35] transition-colors"
                  data-testid="story-image-btn"
                >
                  <ImagePlus className="w-4 h-4" />
                  Photo/Video
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/mp4,video/webm,video/quicktime"
                  onChange={handleImageSelect}
                  className="hidden"
                  data-testid="story-image-input"
                />
                <span className="text-xs text-muted-foreground">{newContent.length}/280</span>
              </div>
              <button
                onClick={handleCreate}
                disabled={loading || (!newContent.trim() && !imageFile)}
                className="bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                data-testid="story-submit-btn"
              >
                {loading ? "..." : "Publier"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story viewer */}
      {viewing !== null && groups[viewing.groupIdx] && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
          data-testid="story-viewer"
        >
          {(() => {
            const group = groups[viewing.groupIdx];
            const story = group.stories[viewing.storyIdx];
            if (!story) { setViewing(null); return null; }
            return (
              <div className="relative w-full max-w-md h-[85vh] flex flex-col">
                {/* Progress bars */}
                <div className="flex gap-1 px-4 pt-4">
                  {group.stories.map((_, i) => (
                    <div key={i} className="flex-1 h-0.5 rounded-full bg-white/20 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-white transition-none"
                        style={{
                          width: i < viewing.storyIdx ? "100%" : i === viewing.storyIdx ? `${progress}%` : "0%",
                          backgroundColor: i === viewing.storyIdx ? "#FF6B35" : "white",
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Header */}
                <div className="flex items-center gap-3 px-4 pt-3 pb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center">
                    {group.artist_avatar ? (
                      <img src={group.artist_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">
                        {(group.artist_name || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-semibold text-sm text-white">{group.artist_name}</p>
                    <p className="text-[10px] text-white/50">{timeAgo(story.created_at)}</p>
                  </div>
                  {story.view_count > 0 && (
                    <div className="flex items-center gap-1 text-white/50 text-[10px]">
                      <Eye className="w-3 h-3" />
                      {story.view_count}
                    </div>
                  )}
                  <button
                    onClick={() => setViewing(null)}
                    className="text-white/70 hover:text-white z-10"
                    data-testid="story-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
                  {story.media_url && story.media_type === "video" ? (
                    <video
                      src={story.media_url}
                      className="max-w-full max-h-[60vh] rounded-2xl object-contain mb-4"
                      autoPlay
                      loop
                      playsInline
                      muted={false}
                    />
                  ) : story.media_url ? (
                    <img
                      src={story.media_url}
                      alt=""
                      className="max-w-full max-h-[60vh] rounded-2xl object-contain mb-4"
                    />
                  ) : null}
                  {story.content && (
                    <p className={`text-center leading-relaxed whitespace-pre-wrap px-4 ${
                      story.media_url ? "text-sm text-white/80" : "text-lg text-white"
                    }`}>
                      {story.content}
                    </p>
                  )}
                </div>

                {/* Navigation */}
                <div className="absolute inset-0 flex pointer-events-none" style={{top: '80px'}}>
                  <button
                    onClick={prevStory}
                    className="w-1/3 h-full pointer-events-auto"
                    data-testid="story-prev"
                  />
                  <div className="w-1/3" />
                  <button
                    onClick={nextStory}
                    className="w-1/3 h-full pointer-events-auto"
                    data-testid="story-next"
                  />
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
};
